import { useMemo } from 'react';
import {
  ETIQUETA_DESTACADO,
  NIVEL_DESTACADO,
  type Destacado,
  type Lectura,
  type NivelFrase,
  type TipoDestacado,
} from '@suminuta/shared';

/**
 * Resaltado de las filas que la lectura menciona (PROMPT_N°056).
 *
 * Si la frase dice "3 productos no cuadran", la tabla tiene que mostrar
 * cuáles sin que el usuario los busque. Los ids vienen de la misma lectura
 * que escribió la frase, así que no pueden desincronizarse.
 */

export type MapaDestacados = Map<number, TipoDestacado[]>;

export const useDestacados = (lectura?: Lectura): MapaDestacados =>
  useMemo(() => {
    const mapa: MapaDestacados = new Map();
    for (const d of lectura?.destacados ?? ([] as Destacado[])) {
      const previos = mapa.get(d.id) ?? [];
      // Una fila puede estar destacada por más de un motivo (top valor y
      // bajo mínimo a la vez); se muestran todas sus etiquetas.
      if (!previos.includes(d.tipo)) previos.push(d.tipo);
      mapa.set(d.id, previos);
    }
    return mapa;
  }, [lectura]);

/** El nivel más grave entre los motivos de una fila manda el color. */
const PRIORIDAD: NivelFrase[] = ['alerta', 'atencion', 'info', 'ok'];

export const nivelDeFila = (
  tipos: TipoDestacado[] | undefined
): NivelFrase | null => {
  if (!tipos || tipos.length === 0) return null;
  const niveles = tipos.map((t) => NIVEL_DESTACADO[t]);
  return PRIORIDAD.find((n) => niveles.includes(n)) ?? null;
};

const FONDO_FILA: Record<NivelFrase, string> = {
  alerta: 'bg-red-50 dark:bg-red-900/20',
  atencion: 'bg-amber-50 dark:bg-amber-900/20',
  info: 'bg-blue-50/60 dark:bg-blue-900/10',
  ok: '',
};

/** Clases de la fila resaltada; cadena vacía si no está destacada. */
export const claseFila = (tipos: TipoDestacado[] | undefined): string => {
  const nivel = nivelDeFila(tipos);
  return nivel ? FONDO_FILA[nivel] : '';
};

const TONO_ETIQUETA: Record<NivelFrase, string> = {
  alerta:
    'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  atencion:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  ok: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};

/** Etiquetas cortas al lado del nombre: "Top valor", "No cuadra", etc. */
export function EtiquetasDestacado({ tipos }: { tipos?: TipoDestacado[] }) {
  if (!tipos || tipos.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap gap-1 ml-2 align-middle">
      {tipos.map((t) => (
        <span
          key={t}
          className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
            TONO_ETIQUETA[NIVEL_DESTACADO[t]]
          }`}
        >
          {ETIQUETA_DESTACADO[t]}
        </span>
      ))}
    </span>
  );
}
